import * as angular from 'angular';
import * as _ from 'lodash';

import BaseResource from '../models/base-resource';
import JsonApiMessage from '../models/jsonapi-message';
import JsonApiReference from '../models/jsonapi-reference';
import JsonApiResource from '../models/jsonapi-resource';
import ReferenceResolver from '../models/reference-resolver';

interface ResourceConstructor<T extends BaseResource> {
  new (): T;
  new (init: Partial<BaseResource>): T;
}

interface ParserMap {
  [resourceType: string]: any;
}

interface ResourceIndexById {
  [resourceId: string]: JsonApiResource;
}
interface ResourceIndex {
  [resourceType: string]: ResourceIndexById;
}

interface ResourceCacheById {
  [resourceId: string]: BaseResource;
}
interface ResourceCache {
  [resourceType: string]: ResourceCacheById;
}

export default class JsonApiParser {
  private includes: ResourceIndex = {};
  private cache: ResourceCache = {};
  private static parsers: ParserMap = {};

  // TODO: Split this stuff out into a resource factory
  static instantiate<T extends BaseResource>(resourceType: { new (): T }): T {
    return new resourceType();
  }

  static addResourceConstructor<T extends BaseResource>(resourceType: ResourceConstructor<T>) {
    const instance: T = JsonApiParser.instantiate(resourceType); //new resourceType();

    JsonApiParser.parsers[instance.type] = resourceType;
  }

  static getResourceConstructor<T extends BaseResource>(
    resource: JsonApiReference
  ): ResourceConstructor<T> {
    return JsonApiParser.parsers[resource.type];
  }

  /// Parse a jsonapi message
  parse<T extends BaseResource>(data): T | T[] {
    const message = new JsonApiMessage(data);
    let result;

    this.includes = this.indexIncludes(message.included);

    if (message.data instanceof Array) {
      result = _.map(message.data, data => this.unpack<T>(data));
    } else {
      result = this.unpack<T>(message.data);
    }

    if (!_.isNil(data.meta)) {
      result.meta = data.meta;
    }

    this.clear();

    return result;
  }

  /// Unpack a jsonapi reference, and return the right resource type
  unpack<T extends BaseResource>(init: JsonApiReference): T {
    let item;
    const cached: BaseResource = this.getCachedResource(init);

    if (cached) {
      // We've seen this id/type before
      item = cached;

      if (item.unpacked || !(init instanceof JsonApiResource)) {
        // It's already hydrated, and that's not a resource, so we're not getting
        //  any more data. Just return it.
        // console.log(`Got cached: {id: ${init.id}, type: '${init.type}'}`);
        return item;
      }
      // We must have just seen a reference before. Continue on to hydrated
      //  the object we previously issued, and everybody will get the data
      //  thanks to the magic of object references!
    } else {
      // Haven't seen this yet. Gin up a new one
      const MyConstructor: ResourceConstructor<T> = JsonApiParser.getResourceConstructor<T>(init);
      if (_.isNil(MyConstructor)) {
        // We don't have a resouce model for this yet
        console.error(`Unhandled resource type: '${init.type}'`);
        console.error(`You probably need to add it to jsonapi-parser.ts`);
        return undefined;
      }

      // Construct a new instance of the resource model
      item = new MyConstructor(<Partial<BaseResource>>{ id: init.id, type: init.type });
    }

    // Do this here so that we don't get into a loop with cyclical relationships!
    this.cacheIncludedResource(item);

    // Unpack any data and relationships into the model
    item.unpack(init, this.resolve.bind(this));

    return item;
  }

  /// Create an index of included resources
  indexIncludes(includes: JsonApiResource[]): any {
    let result = {};
    _.each(includes, include => {
      let group: any = result[include.type] || {};
      group[include.id] = include;

      result[include.type] = group;
    });

    return result;
  }

  /// Add a unpacked resource to the cache
  cacheIncludedResource(resource: BaseResource): void {
    if (_.isNil(this.cache[resource.type])) {
      this.cache[resource.type] = {};
    }

    this.cache[resource.type][resource.id] = resource;
  }

  /// Try to get a cached resource, given a reference
  getCachedResource(ref: JsonApiReference): BaseResource {
    let result: BaseResource =
      !_.isNil(ref) && !_.isNil(this.cache[ref.type]) ? this.cache[ref.type][ref.id] : undefined;

    return result;
  }

  /// Retrieve an included resource, given a reference
  getIncluded(ref: JsonApiReference): JsonApiResource {
    let result: JsonApiResource = !_.isNil(this.includes[ref.type])
      ? this.includes[ref.type][ref.id]
      : undefined;

    return result;
  }

  /// Resolve a reference, and return the unpacked resource
  resolve(ref: JsonApiReference): BaseResource;
  resolve(ref: JsonApiReference[]): BaseResource[];
  resolve(ref: any): any {
    if (_.isNil(ref)) {
      return;
    }

    if (ref instanceof Array) {
      const result: BaseResource[] = _.map(ref, r => this.resolve(r));
      return result;
    } else {
      const cached: BaseResource = this.getCachedResource(ref);
      if (!_.isNil(cached)) {
        return cached;
      }

      const included: JsonApiResource = this.getIncluded(ref);
      let result = !_.isNil(included) ? this.unpack(included) : this.unpack(ref);

      this.cacheIncludedResource(result);

      return result;
    }
  }

  /// Clear cached data
  private clear() {
    this.cache = {};
    this.includes = {};
  }
}

// Register models with the parser. This sucks, but I can't think of a better way!
// TODO: Think of a better way to do this
import ManufacturerResource from '../models/manufacturer-resource';
import PermissionResource from '../models/permission-resource';
import RoleResource from '../models/role-resource';
import SiteResource from '../models/site-resource';
import SubscriptionResource from '../models/subscription-resource';
import UserResource from '../models/user-resource';
import UserRoleResource from '../models/user-role-resource';
import ReportResource from '../models/report-resource';
import EpxUnitResource from '../models/epx-unit-resource';
import ReportScheduleResource from '../models/report-schedule-resource';

JsonApiParser.addResourceConstructor(PermissionResource);
JsonApiParser.addResourceConstructor(RoleResource);
JsonApiParser.addResourceConstructor(SubscriptionResource);
JsonApiParser.addResourceConstructor(UserResource);
JsonApiParser.addResourceConstructor(UserRoleResource);
JsonApiParser.addResourceConstructor(ReportResource);
JsonApiParser.addResourceConstructor(SiteResource);
JsonApiParser.addResourceConstructor(EpxUnitResource);
JsonApiParser.addResourceConstructor(ManufacturerResource);
JsonApiParser.addResourceConstructor(ReportScheduleResource);

angular.module('ciApp.services').service('JsonApiParser', [JsonApiParser]);



// WEBPACK FOOTER //
// ./common/services/jsonapi-parser.ts